import { useCallback } from 'react';
import { createContext, useContext, useState, ReactNode } from 'react';

import { SequenciesResponse, Sequency } from '~/models/Sequencies';
import api from '~/services/api';

type SequencyData = {
  sequencies: Sequency[];
  updateSequencies: (divisionId: number | string) => void;
  selectedSequency: Sequency;
  updateSequency: (data: Sequency) => void;
  sequencyLabel: string;
  setSequencyLabel: (data: string) => void;
};

export const SequencyContext = createContext({} as SequencyData);

type Props = {
  children: ReactNode;
};

export function SequencyProvider({ children }: Props) {
  const [sequencies, setSequencies] = useState<Sequency[]>([]);
  const [selectedSequency, setSequency] = useState<Sequency>({} as Sequency);
  const [sequencyLabel, setSequencyLabel] = useState<string>(``);

  function updateSequency(data: Sequency) {
    setSequency(data);
  }

  const updateSequencies = useCallback(async (divisionId: number | string) => {
    const { data } = await api.get<SequenciesResponse>(
      `training-programs/${divisionId}/divisions/${divisionId}/sequencies`,
    );

    setSequencies(data.results);
  }, []);

  return (
    <SequencyContext.Provider
      value={{
        sequencies,
        updateSequencies,
        selectedSequency,
        updateSequency,
        sequencyLabel,
        setSequencyLabel,
      }}
    >
      {children}
    </SequencyContext.Provider>
  );
}

export const useSequency = (): SequencyData => {
  const context = useContext(SequencyContext);

  if (!context) {
    throw new Error(`useSequency must be use within a SequencyProvider!`);
  }

  return context;
};
